/**
 * Participant Detail Page - Monitoring & Progress Dossier
 *
 * Deep-dive into an individual's academic trajectory, evaluation scores,
 * and scholarly reflections.
 * Built with Operational Architect aesthetic: editorial, high-density, precise.
 */
import { useParams, Link } from 'react-router';
import { useState } from 'react';
import {
  MessageSquare,
  History,
  ChevronRight,
  Clock,
  ShieldCheck,
  MoreVertical,
  CheckCircle2,
  AlertTriangle,
  Zap,
  BarChart3,
} from 'lucide-react';
import { useEnrollment } from '../hooks/useEnrollments';
import { useReflections } from '../hooks/useReflections';
import { TranscriptModal } from '../components/TranscriptModal';
import { Card, Button, LoadingSpinner } from '@lms/ui';
import { useAuthStore } from '../store/auth';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import { getGradePredikat } from '@lms/shared';
export function ParticipantDetail() {
  const { id } = useParams<{ id: string }>();
  const { user: admin } = useAuthStore();
  const { language, t } = useLanguage();
  const { data: enrollment, isLoading: enrollmentLoading } = useEnrollment(id || '');
  const { data: reflections, isLoading: reflectionsLoading } = useReflections({
    userId: enrollment?.userId,
  });

  const [showTranscript, setShowTranscript] = useState(false);
  const now = new Date();
  const todayStr = now.toLocaleDateString('id-ID');

  if (enrollmentLoading || reflectionsLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
        <p className="text-sm font-body text-on-surface-variant mt-4">
          <Bilingual {...copy.participantDetail.loadingTelemetry} />
        </p>
      </div>
    );
  }

  if (!enrollment) {
    return (
      <div className="p-12 text-center bg-surface-container-low rounded-[2rem] border border-on-surface/5">
        <AlertTriangle className="text-error mx-auto mb-4" size={48} />
        <h2 className="text-xl font-headline font-bold text-on-surface">
          <Bilingual {...copy.participantDetail.notFoundTitle} />
        </h2>
        <p className="text-on-surface-variant mt-2 max-w-xs mx-auto">
          <Bilingual {...copy.participantDetail.notFoundDesc} />
        </p>
        <Link
          to="/participants"
          className="mt-8 inline-block text-primary font-bold uppercase tracking-widest text-[10px] hover:underline"
        >
          <Bilingual {...copy.participantDetail.btnReturnDirectory} />
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-12 animate-fadeIn max-w-7xl mx-auto">
      {/* Editorial Header */}
      <section className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 pb-8 border-b border-outline-variant/10">
        <div>
          <div className="flex items-center gap-2 text-on-surface-variant text-[10px] font-label font-bold uppercase tracking-widest mb-6">
            <Link to="/participants" className="hover:text-primary transition-colors">
              <Bilingual {...copy.participantDetail.monitoring} />
            </Link>
            <ChevronRight size={12} />
            <span className="text-primary font-black">{enrollment.user?.name}</span>
          </div>
          <div className="flex items-center gap-4">
            <h1 className="text-5xl font-extrabold font-headline tracking-tighter text-on-surface m-0 leading-none">
              {enrollment.user?.name}
            </h1>
            <div className="px-3 py-1 bg-primary/5 text-primary font-bold text-[10px] uppercase tracking-widest rounded-lg border border-primary/10">
              <Bilingual {...copy.participantDetail.activeStatus} />
            </div>
          </div>
          <p className="text-xl font-body text-on-surface-variant mt-4 leading-relaxed italic opacity-80 border-l-4 border-secondary/30 pl-4">
            {t(copy.participantDetail.seniorScholarLabel)} • {enrollment.program?.name}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="ghost"
            onClick={() => setShowTranscript(true)}
            className="px-6 border border-primary/20 text-primary font-bold text-[10px] uppercase tracking-widest rounded-xl hover:bg-primary/5 flex items-center gap-1.5"
          >
            <History size={14} />
            <span>Transkrip Riwayat</span>
          </Button>
          <Button
            variant="ghost"
            onClick={() =>
              alert(
                t(copy.participantDetail.exportDossierAlert).replace(
                  '{name}',
                  enrollment.user?.name || ''
                )
              )
            }
            className="px-6 border border-outline-variant/30 font-bold text-[10px] uppercase tracking-widest rounded-xl"
          >
            <Bilingual {...copy.participantDetail.btnExportDossier} />
          </Button>
          <Button
            onClick={() => alert(t(copy.participantDetail.modifyPathAlert))}
            className="px-6 shadow-level2 font-bold text-[10px] uppercase tracking-[0.2em] rounded-xl"
          >
            <Bilingual {...copy.participantDetail.btnModifyPath} />
          </Button>
        </div>
      </section>

      {/* Main Dossier Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
        {/* Left Column: Progress & Evaluations (Span 8) */}
        <div className="lg:col-span-8 space-y-10">
          {/* Curriculum Mastery */}
          <Card className="border border-on-surface/5 p-10 space-y-10">
            <div className="flex justify-between items-end border-b border-outline-variant/10 pb-6">
              <div>
                <h3 className="text-xl font-headline font-bold text-primary m-0">
                  <Bilingual {...copy.participantDetail.masteryTitle} />
                </h3>
                <p className="text-xs font-body text-on-surface-variant mt-1">
                  <Bilingual {...copy.participantDetail.masteryDesc} />
                </p>
              </div>
              <span className="text-sm font-bold text-on-surface-variant">
                <Bilingual {...copy.participantDetail.masteryOverall} />{' '}
                <span className="text-secondary">{Math.round(enrollment.progress)}%</span>{' '}
                <Bilingual {...copy.participantDetail.masteryComplete} />
              </span>
            </div>

            <div className="space-y-10">
              {(enrollment.program?.levels || [])
                .sort((a, b) => a.order - b.order)
                .map(level => {
                  const levelMaterials = level.flows?.flatMap(f => f.materials || []) || [];
                  const requiredMaterials = levelMaterials.filter(m => m.isRequired);
                  const hasRequired = requiredMaterials.length > 0;

                  const totalCount = hasRequired
                    ? requiredMaterials.length
                    : levelMaterials.length || 1;

                  const completedInLevel =
                    enrollment.completions?.filter(c =>
                      levelMaterials.some(m => m.id === c.materialId)
                    ) || [];

                  const completedCount = hasRequired
                    ? completedInLevel.filter(c =>
                        requiredMaterials.some(m => m.id === c.materialId)
                      ).length
                    : completedInLevel.length;

                  const levelProgress = Math.min(
                    Math.round((completedCount / totalCount) * 100),
                    100
                  );

                  return (
                    <div key={level.id} className="space-y-4">
                      <div
                        className={`group cursor-pointer bg-surface-container-low/30 hover:bg-surface-container-low p-4 rounded-2xl border border-on-surface/5 transition-all
                        ${levelProgress === 0 ? 'opacity-40' : ''}`}
                        onClick={() => {
                          const el = document.getElementById(`level-detail-${level.id}`);
                          if (el) el.classList.toggle('hidden');
                        }}
                      >
                        <div className="flex justify-between items-center text-[11px] font-bold uppercase tracking-widest text-on-surface mb-3">
                          <div className="flex items-center gap-3">
                            <span className="text-primary/60">
                              <Bilingual {...copy.participantDetail.phaseLabel} />{' '}
                              {String(level.order).padStart(2, '0')}
                            </span>
                            <span>{level.name}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={levelProgress === 100 ? 'text-primary' : ''}>
                              {levelProgress}%
                            </span>
                            <ChevronRight
                              size={14}
                              className="group-hover:translate-x-1 transition-transform"
                            />
                          </div>
                        </div>
                        <div className="h-2 w-full bg-surface-container-high rounded-full overflow-hidden">
                          <div
                            className={`h-full ${levelProgress === 100 ? 'bg-primary' : levelProgress > 0 ? 'bg-secondary' : 'bg-outline-variant'} transition-all duration-1000`}
                            style={{ width: `${levelProgress}%` }}
                          />
                        </div>
                      </div>

                      {/* Level Detail Explorer (Asset List) */}
                      <div
                        id={`level-detail-${level.id}`}
                        className="hidden pl-8 pr-4 py-2 space-y-2 animate-fadeIn border-l-2 border-primary/10 ml-4"
                      >
                        {levelMaterials.length === 0 ? (
                          <p className="text-[10px] italic text-on-surface-variant/40">
                            <Bilingual {...copy.participantDetail.noAssets} />
                          </p>
                        ) : (
                          levelMaterials.map(m => {
                            const isCompleted = enrollment.completions?.some(
                              c => c.materialId === m.id
                            );
                            return (
                              <div
                                key={m.id}
                                className="flex justify-between items-center py-2 border-b border-on-surface/5 last:border-0 grow"
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className={
                                      isCompleted ? 'text-primary' : 'text-on-surface-variant/30'
                                    }
                                  >
                                    {isCompleted ? (
                                      <CheckCircle2 size={14} />
                                    ) : (
                                      <div className="w-3.5 h-3.5 rounded-full border border-current" />
                                    )}
                                  </div>
                                  <span
                                    className={`text-[11px] font-medium ${isCompleted ? 'text-on-surface font-bold' : 'text-on-surface-variant'}`}
                                  >
                                    {m.title}
                                    {m.isRequired && (
                                      <span className="ml-2 text-[8px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-md uppercase tracking-tighter">
                                        <Bilingual {...copy.participantDetail.requiredBadge} />
                                      </span>
                                    )}
                                  </span>
                                </div>
                                {isCompleted && (
                                  <span className="text-[9px] font-mono text-primary font-bold">
                                    <Bilingual {...copy.participantDetail.completedBadge} />
                                  </span>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              {(!enrollment.program?.levels || enrollment.program.levels.length === 0) && (
                <div className="py-10 text-center text-xs font-body text-on-surface-variant opacity-40 italic">
                  <Bilingual {...copy.participantDetail.noLevels} />
                </div>
              )}
            </div>
          </Card>

          {/* Evaluation Matrix */}
          <section>
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-headline font-bold text-on-surface m-0 flex items-center gap-3">
                <BarChart3 className="text-primary" size={24} />
                <Bilingual {...copy.participantDetail.evaluationMatrix} />
              </h3>
              <button
                onClick={() => setShowTranscript(true)}
                className="text-[10px] font-bold text-primary uppercase tracking-widest hover:underline"
              >
                <Bilingual {...copy.participantDetail.viewTranscript} />
              </button>
            </div>
            <Card className="border border-on-surface/5 !p-0 overflow-hidden">
              <div className="overflow-x-auto w-full">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-surface-container-low/50 text-on-surface-variant border-b border-outline-variant/10">
                      <th className="px-8 py-4 text-[10px] font-black uppercase tracking-widest">
                        <Bilingual {...copy.participantDetail.tableHeaderNode} />
                      </th>
                      <th className="px-8 py-4 text-center text-[10px] font-black uppercase tracking-widest">
                        <Bilingual {...copy.participantDetail.tableHeaderCalibration} />
                      </th>
                      <th className="px-8 py-4 text-right text-[10px] font-black uppercase tracking-widest">
                        <Bilingual {...copy.participantDetail.tableHeaderScore} />
                      </th>
                      <th className="px-8 py-4 text-center text-[10px] font-black uppercase tracking-widest">
                        <Bilingual {...copy.participantDetail.tableHeaderStatus} />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-on-surface/5 font-body">
                    {(
                      enrollment as unknown as {
                        results: {
                          title: string;
                          createdAt: string;
                          score: number;
                          maxScore: number;
                        }[];
                      }
                    ).results?.map((result, i: number) => (
                      <tr key={i} className="hover:bg-surface-container-low/50 transition-colors">
                        <td className="px-8 py-5 font-bold text-sm text-on-surface">
                          {result.title}
                        </td>
                        <td className="px-8 py-5 text-center text-xs text-on-surface-variant font-medium">
                          {new Date(result.createdAt).toLocaleDateString()}
                        </td>
                        <td
                          className={`px-8 py-5 text-right font-black ${result.score >= result.maxScore * 0.8 ? 'text-primary' : 'text-on-surface'}`}
                        >
                          <div className="flex flex-col items-end gap-1">
                            <span>
                              {Math.round(result.score)}/{result.maxScore}
                            </span>
                            <span className="text-[9px] uppercase tracking-widest opacity-60">
                              {language === 'ar'
                                ? getGradePredikat(result.score).arabic
                                : getGradePredikat(result.score).indonesian}
                            </span>
                          </div>
                        </td>
                        <td className="px-8 py-5 text-center">
                          <span
                            className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border
                              ${
                                result.score >= result.maxScore * 0.8
                                  ? 'bg-primary/5 text-primary border-primary/20'
                                  : 'bg-surface-container-high text-on-surface-variant border-outline-variant/20'
                              }`}
                          >
                            {result.score >= result.maxScore * 0.8 ? (
                              <Bilingual {...copy.participantDetail.statusProficient} />
                            ) : (
                              <Bilingual {...copy.participantDetail.statusComplete} />
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {(!(enrollment as unknown as { results: unknown[] }).results ||
                      (enrollment as unknown as { results: unknown[] }).results.length === 0) && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-8 py-10 text-center text-xs italic text-on-surface-variant opacity-40"
                        >
                          <Bilingual {...copy.participantDetail.noResults} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>
        </div>

        {/* Right Column: AI Insights & Timeline (Span 4) */}
        <div className="lg:col-span-4 space-y-10">
          {/* Scholarly Insights (integrated AI style) */}
          <div className="bg-primary text-on-primary p-8 rounded-[2rem] shadow-xl space-y-8 relative overflow-hidden group">
            <div className="absolute -top-4 -right-4 p-4 opacity-[0.05] pointer-events-none group-hover:scale-110 transition-transform">
              <ShieldCheck size={120} />
            </div>
            <div className="relative z-10 space-y-6">
              <div className="flex items-center gap-3">
                <Zap className="text-secondary" size={24} />
                <h3 className="text-xl font-headline font-bold tracking-tight m-0">
                  <Bilingual {...copy.participantDetail.insightsTitle} />
                </h3>
              </div>
              <p className="text-sm font-body leading-relaxed opacity-90">
                {t(copy.participantDetail.insightsDesc).replace(
                  '{name}',
                  enrollment.user?.name || ''
                )}
              </p>
              <div className="p-6 bg-black/10 rounded-2xl space-y-4 border border-white/10">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-secondary">
                  <Bilingual {...copy.participantDetail.insightsRecInterventions} />
                </p>
                <p className="text-xs italic opacity-60 m-0">
                  <Bilingual {...copy.participantDetail.insightsAwaitingData} />
                </p>
              </div>
              <Button
                variant="ghost"
                onClick={() => alert(t(copy.participantDetail.recalibrateAlert))}
                className="w-full bg-white text-primary font-black text-[10px] uppercase tracking-[0.2em] rounded-xl hover:bg-white/90"
              >
                <Bilingual {...copy.participantDetail.insightsBtnExecute} />
              </Button>
            </div>
          </div>

          {/* Reflection Feed */}
          <Card className="border border-on-surface/5 p-8 space-y-10">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-headline font-bold text-on-surface m-0">
                <Bilingual {...copy.participantDetail.reflectionsTitle} />
              </h3>
              <MoreVertical size={16} className="text-outline-variant" />
            </div>
            <div className="relative pl-8 space-y-10 before:absolute before:left-[11px] before:top-2 before:bottom-0 before:w-px before:bg-outline-variant/30">
              {reflections?.items.map(r => (
                <div key={r.id} className="relative">
                  <div className="absolute -left-[10px] top-1.5 w-5 h-5 rounded-lg bg-surface-container-high border border-outline-variant/20 flex items-center justify-center text-primary">
                    <MessageSquare size={12} />
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-[9px] font-label font-bold text-secondary uppercase tracking-widest">
                      {new Date(r.createdAt).toLocaleDateString('id-ID', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                    <h4 className="text-xs font-bold text-on-surface m-0">
                      {r.material?.title || t(copy.participantDetail.materialReflectionFallback)}
                    </h4>
                    <p className="text-[11px] font-body text-on-surface-variant italic leading-relaxed line-clamp-2">
                      "{r.response}"
                    </p>
                  </div>
                </div>
              ))}
              {(!reflections || reflections.meta.total === 0) && (
                <div className="text-center py-10 opacity-30 italic text-xs">
                  <Bilingual {...copy.participantDetail.noReflections} />
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              onClick={() => alert(t(copy.participantDetail.openArchiveAlert))}
              className="w-full text-[10px] font-black uppercase tracking-widest text-primary hover:underline"
            >
              <Bilingual {...copy.participantDetail.viewAnalyticalArchive} />
            </Button>
          </Card>

          {/* Rapid Metrics */}
          <div className="grid grid-cols-1 gap-6">
            <div className="bg-surface-container-low p-6 rounded-2xl border border-on-surface/5 flex items-center gap-5">
              <div className="p-4 bg-primary/10 rounded-xl text-primary">
                <Clock size={28} />
              </div>
              <div>
                <p className="text-[9px] font-black text-on-surface-variant uppercase tracking-widest mb-1">
                  <Bilingual {...copy.participantDetail.metricDuration} />
                </p>
                <p className="text-2xl font-headline font-black text-on-surface m-0">
                  {Math.ceil(
                    (now.getTime() - new Date(enrollment.startedAt).getTime()) /
                      (1000 * 60 * 60 * 24)
                  )}{' '}
                  <span className="text-[10px] font-bold opacity-40">
                    <Bilingual {...copy.participantDetail.metricActiveDays} />
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transcript Modal */}
      {showTranscript && enrollment?.userId && (
        <TranscriptModal
          userId={enrollment.userId}
          userName={enrollment.user?.name || ''}
          onClose={() => setShowTranscript(false)}
        />
      )}

      {/* Audit Footnote */}
      <footer className="pt-12 border-t border-outline-variant/10 flex flex-col md:flex-row justify-between items-center gap-6 opacity-40">
        <div className="flex items-center gap-3">
          <History size={16} />
          <p className="text-[10px] font-body font-medium m-0 tracking-wide">
            {t(copy.participantDetail.auditLog)
              .replace('{admin}', admin?.name || '')
              .replace('{date}', todayStr)}
          </p>
        </div>
        <div className="flex gap-8">
          {(
            [
              { key: 'transcript', label: copy.participantDetail.footerTranscriptLabel },
              { key: 'audit', label: copy.participantDetail.footerAuditLabel },
              { key: 'sync', label: copy.participantDetail.footerSyncLabel },
            ] as const
          ).map(a => (
            <button
              key={a.key}
              onClick={() => {
                if (a.key === 'transcript') {
                  setShowTranscript(true);
                } else {
                  alert(
                    t(copy.participantDetail.syncSuccessAlert).replace('{feature}', t(a.label))
                  );
                }
              }}
              className="text-[10px] font-label font-bold uppercase tracking-widest hover:text-primary transition-colors"
            >
              <Bilingual {...a.label} />
            </button>
          ))}
        </div>
      </footer>
    </div>
  );
}
